import { useContext } from 'react'

import {
  Button, Columns, LoadingModule, SaveFormModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useProvider, { useProviderForm } from '@hooks/useProvider'

import Contracts from '@components/Contracts'
import CustomLinks from '@components/CustomLinks'
import EntityActivity from '@components/EntityActivity'
import EntityAssets from '@components/EntityAssets'
import MessagePanel from '@components/MessagePanel'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import Address from '../Address'
import Billing from '../Billing'
import CustomInvoice from '../CustomInvoice'
import Details from '../Details'
import Logos from '../Logos'
import PageLayout from '../../../../components/PageLayout'
import Payments from '../Payments'
import ProviderData from '../ProviderData'
import ProviderTools from '../../../../components/ProviderTools'

const PageContent = (props: { providerId: number }) => {
  const { providerId } = props

  const providerPayload = useProvider({ id: providerId })
  const {
    callbacks: {
      updateProvider: updateFn,
      createOrEditCustomLinks,
    },
    hasQuickLinks,
    loading,
    provider,
    quickLinks,
    updating,
  } = providerPayload

  const zuoraAccountNumber = provider?.zuora_account_number || null

  const providerForm = useProviderForm(provider)
  const { entityState, saveEnabled } = providerForm

  const pageContext = useContext(PageContext)
  const {
    callbacks,
    callbacks: {
      deleteContract,
      updateProvider,
    },
  } = pageContext

  const updateProviderPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.provider,
    toastText: 'Provider Updated',
  }

  return (
    <PageLayout entityPayload={providerPayload} activeTabBarItemKey="details">
      <Columns.Main>
        {/*
         // @ts-expect-error TS2322 */}
        <LoadingModule loading={loading} times={3} />

        {!loading && (
          <>
            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              {!zuoraAccountNumber && (
              <MessagePanel
                description={`This provider does not have a Zuora Account Number stored
                  which means no invoices can be generated against this provider. If
                  this provider should be invoiced for any reason, please have the
                  provider added to Salesforce. Reach out to the Realhub Onboarding 
                  Specialist team to correctly link this account to Salesforce & Zuora.`}
                entityProperty
                marginBottom="large"
                title="Non Billable Provider"
              />
              )}
            </ScopedConditionalContent>

            <Details providerForm={providerForm} />

            <Address providerForm={providerForm} />

            <Payments provider={provider} providerForm={providerForm} />

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <ProviderData providerForm={providerForm} />

              <Billing providerForm={providerForm} />
            </ScopedConditionalContent>

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                onClick={() => updateProvider(updateProviderPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>

            <EntityActivity subjectId={provider.id} subjectType="Provider" />
          </>
        )}
      </Columns.Main>

      <Columns.Sidebar>
        <ProviderTools providerPayload={providerPayload} />

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <Logos providerPayload={providerPayload} />

          <Contracts callbacks={{ deleteContract }} ownerId={providerId} ownerType="Provider" />
        </ScopedConditionalContent>

        <CustomLinks
          callbacks={{
            createOrEditCustomLinks: () => createOrEditCustomLinks({
              callbacks: {
                updateEntity: updateFn,
              },
            }),
          }}
          hasQuickLinks={hasQuickLinks}
          loading={loading!}
          quickLinks={quickLinks}
        />

        <CustomInvoice providerPayload={providerPayload} />

        <EntityAssets
          callbacks={callbacks}
          filters={{ attachableId: providerId, attachableType: 'Provider' }}
        />
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
